/**
 * Domain module for multimedia upload constraints.
 *
 * Defines accepted MIME types, file size limits, agent routing,
 * and MIME normalization. Pure functions, no infrastructure imports.
 */

// ─── Types ──────────────────────────────────────────────────────────

export type AcceptedMediaType =
  (typeof MEDIA_CONSTRAINTS.acceptedTypes)[number];

// ─── Constants ──────────────────────────────────────────────────────

export const MEDIA_CONSTRAINTS = {
  acceptedTypes: [
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/pdf",
    "audio/webm",
    "audio/mpeg",
    "text/plain",
  ],
  /** Images at or below this size use the existing base64 inline WS path */
  inlineBase64ThresholdBytes: 500 * 1024, // 500KB
  /** Per-file upload size limit */
  maxSizeBytes: 10 * 1024 * 1024, // 10MB
} as const;

/** Maps non-standard browser MIME strings to their canonical equivalents. */
export const MIME_ALIASES: Record<string, AcceptedMediaType> = {
  "audio/mp3": "audio/mpeg",
};

export const MIME_TO_AGENT: Record<
  AcceptedMediaType,
  "audio_read" | "vision_read"
> = {
  "application/pdf": "vision_read",
  "audio/mpeg": "audio_read",
  "audio/webm": "audio_read",
  "image/jpeg": "vision_read",
  "image/png": "vision_read",
  "image/webp": "vision_read",
  "text/plain": "vision_read",
};

export const MIME_TO_EXTENSION: Record<AcceptedMediaType, string> = {
  "application/pdf": "pdf",
  "audio/mpeg": "mp3",
  "audio/webm": "webm",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "text/plain": "txt",
};

export const SOP_ACCEPTED_TYPES: ReadonlyArray<AcceptedMediaType> = [
  "application/pdf",
  "text/plain",
] as const;

export const SOP_MAX_FILE_COUNT = 10;

/**
 * Validates a candidate set of SOP-intake files. Pure — no side effects.
 * Returns a human-readable error string, or `null` if the set is acceptable.
 * Shared between the SopDropZone (sync pre-upload guard) and `useChat`'s
 * `sendSopFiles` (defensive guard).
 */
export function validateSopFiles(
  files: ReadonlyArray<{ name: string; type: string; size: number }>,
): string | null {
  if (files.length > SOP_MAX_FILE_COUNT) return "Up to 10 files at once.";
  for (const f of files) {
    const t = normalizeMimeType(f.type) as AcceptedMediaType;
    if (!SOP_ACCEPTED_TYPES.includes(t)) {
      return `Only PDF and text files are supported in v1. ("${f.name}")`;
    }
    if (f.size > MEDIA_CONSTRAINTS.maxSizeBytes) {
      return `File "${f.name}" exceeds 10 MB.`;
    }
  }
  return null;
}

// ─── Functions ──────────────────────────────────────────────────────

export function isAcceptedMediaType(
  type: string,
): type is AcceptedMediaType {
  return (MEDIA_CONSTRAINTS.acceptedTypes as readonly string[]).includes(
    type,
  );
}

export function normalizeMimeType(type: string): string {
  return MIME_ALIASES[type] ?? type;
}
